import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Free shipping progress bar component.
 * Shows how much more the customer needs to spend for free shipping.
 * Updates instantly on any cart change (add, remove, quantity update).
 *
 * Uses data-state attribute to toggle SVG icons via CSS:
 *   - empty: cart total is 0 → truck icon (muted)
 *   - progress: cart has items but below threshold → truck icon (active)
 *   - reached: cart meets or exceeds threshold → gift icon (green)
 */
class FreeShippingBarComponent extends Component {
  requiredRefs = ['message', 'messageText', 'progressFill'];

  /** @type {number} Threshold in minor units (cents) */
  #threshold = 0;

  /** @type {string} Shopify money format template */
  #moneyFormat = '';

  /** @type {string} Currency code */
  #currency = '';

  /** @type {string} Message when cart is empty */
  #msgEmpty = '';

  /** @type {string} Message template with {{ amount }} placeholder */
  #msgRemaining = '';

  /** @type {string} Message when threshold is reached */
  #msgReached = '';

  /**
   * Handles cart:update events from both CartAddEvent and CartUpdateEvent.
   * CartAddEvent dispatches with an empty resource, so we fetch /cart.js.
   * CartUpdateEvent (remove/quantity change) includes the full cart object.
   */
  #handleCartUpdate = async (event) => {
    const cart = event.detail?.resource;

    if (cart && typeof cart.total_price === 'number') {
      this.#render(cart.total_price);
    } else {
      // CartAddEvent has empty resource — fetch fresh cart total
      const freshCart = await this.#fetchCart();
      if (freshCart) {
        this.#render(freshCart.total_price);
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'RON';
    this.#msgEmpty = this.dataset.msgEmpty || '';
    this.#msgRemaining = this.dataset.msgRemaining || '';
    this.#msgReached = this.dataset.msgReached || '';

    // Listen for all cart events (bubbles from product-form, cart-items, etc.)
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Initial render from server-provided cart total
    const initialTotal = parseInt(this.dataset.cartTotal, 10) || 0;
    this.#render(initialTotal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Fetches current cart from Shopify's Cart API.
   * @returns {Promise<Object|null>}
   */
  async #fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Renders the progress bar and message for the given cart total.
   * @param {number} cartTotal - Cart total in minor units (cents)
   */
  #render(cartTotal) {
    const remaining = Math.max(0, this.#threshold - cartTotal);
    const progress = this.#threshold > 0
      ? Math.min(1, cartTotal / this.#threshold)
      : 0;

    // GPU-accelerated bar fill
    this.refs.progressFill.style.transform = `scaleX(${progress})`;

    // Determine state and build message
    let text;
    let state;

    if (cartTotal <= 0) {
      text = this.#msgEmpty;
      state = 'empty';
    } else if (remaining > 0) {
      const formatted = formatMoney(remaining, this.#moneyFormat, this.#currency);
      text = this.#msgRemaining.replace('{{ amount }}', formatted);
      state = 'progress';
    } else {
      text = this.#msgReached;
      state = 'reached';
    }

    // Update data-state for CSS icon toggling
    this.dataset.state = state;

    // Update text only (icons are handled via CSS)
    this.refs.messageText.textContent = text;
  }
}

customElements.define('free-shipping-bar-component', FreeShippingBarComponent);
